import { useRef, useState } from 'react';
import { apiPost } from '@lib/apiClient';
import { compressPhoto } from '@lib/photo-compress';

interface Captured {
  path: string;
  preview: string;
}

/**
 * Capture a device photo of the test card, compress client-side, upload to /api/intake/photo, and
 * keep priors on redo (newest is current). Reports the accumulated SharePoint paths + current path
 * up to the Confirm panel for the submit body.
 */
export function PhotoCapture({
  campaign,
  kitBarcode,
  required,
  onChange,
  label = 'Test device photo',
  kind,
}: {
  campaign: string;
  kitBarcode: string;
  required: boolean;
  onChange: (photos: string[], currentPhoto: string | null) => void;
  label?: string;
  kind?: string;
}) {
  const [shots, setShots] = useState<Captured[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const onFile = async (file: File) => {
    setError(null);
    setBusy(true);
    try {
      const compressed = await compressPhoto(file);
      const res = await apiPost<{ photoPath: string; currentPhoto: string }>('/api/intake/photo', {
        campaign,
        kitBarcode,
        image: compressed.dataUrl,
        kind,
      });
      const photoPath = res.data.photoPath;
      if (!res.ok || typeof photoPath !== 'string') {
        setError((res.data.error as string) || `Upload failed (${res.status})`);
        return;
      }
      const next = [...shots, { path: photoPath, preview: compressed.dataUrl }];
      setShots(next);
      onChange(
        next.map((s) => s.path),
        next[next.length - 1]!.path,
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not process the photo');
    } finally {
      setBusy(false);
    }
  };

  const current = shots[shots.length - 1] ?? null;
  return (
    <div className="card">
      <div className="mb-2 flex items-center justify-between">
        <span className="field-label" style={{ marginBottom: 0 }}>
          {label}{' '}
          {required ? (
            <span style={{ color: 'var(--crimson)' }}>*</span>
          ) : (
            <span style={{ color: 'var(--ink-300)' }}>(optional)</span>
          )}
        </span>
        {shots.length > 0 && (
          <span className="text-xs tab-num" style={{ color: 'var(--ink-400)' }}>
            {shots.length} captured
          </span>
        )}
      </div>

      {current && (
        <img
          src={current.preview}
          alt="Captured test device"
          className="mb-3 w-full rounded-cdx border border-cdx-border-subtle"
        />
      )}

      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) void onFile(f);
          e.target.value = '';
        }}
      />
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        disabled={busy}
        className="btn-bench btn-bench-ghost w-full disabled:opacity-50"
      >
        {busy ? 'Uploading...' : current ? 'Retake / add photo' : 'Take photo'}
      </button>
      {error && <p className="mt-2 text-xs text-cdx-danger">{error}</p>}
    </div>
  );
}
